
import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function StagingTipsPage() {
  const [activeRoom, setActiveRoom] = useState('living-room');
  const [checkedItems, setCheckedItems] = useState<string[]>([]);

  const handleCheckItem = (itemId: string) => {
    setCheckedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const stagingRooms = {
    'living-room': {
      title: 'Living Room',
      icon: 'ri-sofa-line',
      image: 'https://public.readdy.ai/ai/img_res/1183bca090cdd1833b21d9143a63cc9e.jpg',
      tips: [
        { id: 'lr-1', text: 'Remove personal photos and replace with neutral artwork' },
        { id: 'lr-2', text: 'Arrange furniture to create conversation areas and flow' },
        { id: 'lr-3', text: 'Add fresh flowers or plants for natural appeal' },
        { id: 'lr-4', text: 'Use neutral throw pillows and blankets for color coordination' },
        { id: 'lr-5', text: 'Ensure adequate lighting with table lamps and overhead fixtures' },
        { id: 'lr-6', text: 'Remove excess furniture to make the space feel larger' },
        { id: 'lr-7', text: 'Clean and organize entertainment center and bookshelves' }
      ]
    },
    'kitchen': {
      title: 'Kitchen',
      icon: 'ri-restaurant-line',
      image: 'https://readdy.ai/api/search-image?query=Professionally%20staged%20kitchen%20with%20clean%20countertops%2C%20modern%20appliances%2C%20fresh%20flowers%2C%20neutral%20colors%2C%20bright%20lighting%20for%20real%20estate%20photography&width=600&height=400&seq=staging-2&orientation=landscape',
      tips: [
        { id: 'k-1', text: 'Clear all countertops except for a few decorative items' },
        { id: 'k-2', text: 'Deep clean appliances inside and out until they shine' },
        { id: 'k-3', text: 'Replace old cabinet hardware with modern brushed nickel or brass' },
        { id: 'k-4', text: 'Add a bowl of fresh fruit or flowers as a centerpiece' },
        { id: 'k-5', text: 'Organize pantry and cabinets - buyers will look inside' },
        { id: 'k-6', text: 'Update lighting fixtures if they look dated' },
        { id: 'k-7', text: 'Place matching dish towels and trivets for a cohesive look' }
      ]
    },
    'master-bedroom': {
      title: 'Master Bedroom',
      icon: 'ri-hotel-bed-line',
      image: 'https://readdy.ai/api/search-image?query=Elegantly%20staged%20master%20bedroom%20with%20neutral%20bedding%2C%20minimal%20decor%2C%20natural%20lighting%2C%20clean%20and%20spacious%20feel%20for%20real%20estate%20photography&width=600&height=400&seq=staging-3&orientation=landscape',
      tips: [
        { id: 'mb-1', text: 'Invest in high-quality neutral bedding and pillows' },
        { id: 'mb-2', text: 'Remove personal items from nightstands and dressers' },
        { id: 'mb-3', text: 'Add bedside lamps for warm, inviting lighting' },
        { id: 'mb-4', text: 'Keep closets organized and only 2/3 full' },
        { id: 'mb-5', text: 'Add a cozy throw blanket at the foot of the bed' },
        { id: 'mb-6', text: 'Place fresh flowers or a small plant on the dresser' },
        { id: 'mb-7', text: 'Ensure curtains or blinds are clean and properly hung' }
      ]
    },
    'bathroom': {
      title: 'Bathroom',
      icon: 'ri-drop-line',
      image: 'https://readdy.ai/api/search-image?query=Clean%20staged%20bathroom%20with%20fresh%20towels%2C%20minimal%20toiletries%2C%20bright%20lighting%2C%20spa-like%20atmosphere%20for%20real%20estate%20photography&width=600&height=400&seq=staging-4&orientation=landscape',
      tips: [
        { id: 'b-1', text: 'Replace old towels with fluffy white or neutral ones' },
        { id: 'b-2', text: 'Remove all personal toiletries from counters and shower' },
        { id: 'b-3', text: 'Add a small plant or fresh flowers for natural appeal' },
        { id: 'b-4', text: 'Ensure grout is clean and re-caulk if necessary' },
        { id: 'b-5', text: 'Place a decorative soap dispenser and matching accessories' },
        { id: 'b-6', text: 'Add a plush bath mat in a neutral color' },
        { id: 'b-7', text: 'Keep medicine cabinet and drawers organized and minimal' }
      ]
    },
    'exterior': {
      title: 'Curb Appeal',
      icon: 'ri-home-4-line',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20home%20exterior%20with%20manicured%20landscaping%2C%20clean%20walkway%2C%20attractive%20front%20door%2C%20excellent%20curb%20appeal%20for%20real%20estate%20photography&width=600&height=400&seq=staging-5&orientation=landscape',
      tips: [
        { id: 'e-1', text: 'Power wash siding, walkways, and driveway' },
        { id: 'e-2', text: 'Plant colorful flowers in beds and containers' },
        { id: 'e-3', text: 'Paint or stain the front door in an attractive color' },
        { id: 'e-4', text: 'Add outdoor lighting for evening showings' },
        { id: 'e-5', text: 'Trim bushes and trees for a well-maintained look' },
        { id: 'e-6', text: 'Replace or update house numbers and mailbox' },
        { id: 'e-7', text: 'Add a welcoming doormat and seasonal decorations' }
      ]
    }
  };

  const quickWins = [
    { icon: 'ri-paint-brush-line', title: 'Fresh Paint', description: 'Neutral colors make spaces feel larger and brighter' },
    { icon: 'ri-lightbulb-line', title: 'Maximize Light', description: 'Open curtains, clean windows, add lamps' },
    { icon: 'ri-delete-bin-line', title: 'Declutter Everything', description: 'Remove 50% of furniture and personal items' },
    { icon: 'ri-leaf-line', title: 'Add Greenery', description: 'Fresh flowers and plants bring life to any space' },
    { icon: 'ri-sparkling-line', title: 'Deep Clean', description: 'Every surface should sparkle for showings' },
    { icon: 'ri-palette-line', title: 'Neutral Palette', description: 'Stick to whites, grays, and beiges throughout' }
  ];

  const currentRoom = stagingRooms[activeRoom as keyof typeof stagingRooms];
  const completedCount = checkedItems.length;
  const totalTips = Object.values(stagingRooms).reduce((sum, room) => sum + room.tips.length, 0);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-black mb-4">
              Professional Home Staging Tips
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your home into a buyer's dream with these proven staging strategies. Properly staged homes sell 73% faster and for 10% more than non-staged homes.
            </p>
          </div>

          {/* Progress Tracker */}
          <div className="bg-black text-white rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Your Staging Progress</h3>
              <div className="text-yellow-500 font-bold">
                {completedCount} / {totalTips} Complete
              </div>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div 
                className="bg-white h-3 rounded-full transition-all duration-300"
                style={{ width: `${(completedCount / totalTips) * 100}%` }}
              ></div>
            </div>
            <p className="text-gray-300 text-sm mt-2">
              Complete all staging tips to maximize your home's appeal and selling potential.
            </p>
          </div>

          {/* Quick Wins Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-black mb-8 text-center">Quick Staging Wins</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickWins.map((win, index) => (
                <div key={index} className="bg-white border-2 border-yellow-500 rounded-lg p-6 hover:border-yellow-400 transition-colors">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${win.icon} text-xl text-black`}></i>
                  </div>
                  <h3 className="font-bold text-black mb-2">{win.title}</h3>
                  <p className="text-gray-600 text-sm">{win.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Room-by-Room Guide */}
          <div className="bg-white border-2 border-yellow-500 rounded-lg overflow-hidden">
            <div className="bg-black text-white p-6">
              <h2 className="text-3xl font-bold text-center mb-6">Room-by-Room Staging Guide</h2>
              
              {/* Room Tabs */}
              <div className="flex flex-wrap justify-center gap-2">
                {Object.entries(stagingRooms).map(([key, room]) => (
                  <button
                    key={key}
                    onClick={() => setActiveRoom(key)}
                    className={`px-4 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeRoom === key
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white text-black hover:bg-gray-100'
                    }`}
                  >
                    <i className={`${room.icon} mr-2`}></i>
                    {room.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-8">
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <img
                    src={currentRoom.image}
                    alt={`Staged ${currentRoom.title}`}
                    className="w-full h-80 object-cover object-top rounded-lg mb-6"
                  />
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-bold text-black mb-4 flex items-center">
                      <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
                      Pro Tip for {currentRoom.title}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {activeRoom === 'living-room' && "The living room is often the first space buyers see. Create a welcoming atmosphere that helps them envision their own lifestyle in the space."}
                      {activeRoom === 'kitchen' && "Kitchens sell homes! A clean, organized kitchen suggests the home has been well-maintained and makes buyers excited about cooking and entertaining."}
                      {activeRoom === 'master-bedroom' && "The master bedroom should feel like a peaceful retreat. Buyers need to imagine themselves relaxing and sleeping comfortably in this space."}
                      {activeRoom === 'bathroom' && "Bathrooms should feel spa-like and clean. Even small updates like new towels and accessories can make a big impact on buyer perception."}
                      {activeRoom === 'exterior' && "You only get one chance to make a first impression. Great curb appeal draws buyers in and sets positive expectations for the entire home."}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-black mb-6">
                    {currentRoom.title} Staging Checklist
                  </h3>
                  
                  <div className="space-y-3">
                    {currentRoom.tips.map((tip) => (
                      <label key={tip.id} className="flex items-start space-x-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={checkedItems.includes(tip.id)}
                          onChange={() => handleCheckItem(tip.id)}
                          className="mt-1 h-5 w-5 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                        />
                        <span className={`text-sm transition-colors ${
                          checkedItems.includes(tip.id) 
                            ? 'text-gray-500 line-through' 
                            : 'text-gray-700 group-hover:text-black'
                        }`}>
                          {tip.text}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-semibold text-black mb-2">Room Completion Status</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {currentRoom.tips.filter(tip => checkedItems.includes(tip.id)).length} of {currentRoom.tips.length} completed
                      </span>
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-white h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${(currentRoom.tips.filter(tip => checkedItems.includes(tip.id)).length / currentRoom.tips.length) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-black text-white rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Need Professional Staging Help?</h3>
              <p className="text-gray-300 mb-6">
                While DIY staging can be effective, professional staging services can maximize your home's potential and help you sell faster for top dollar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-yellow-500 text-black px-8 py-3 rounded-lg hover:bg-yellow-400 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Staging Consultation
                </button>
                <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-black transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
